import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useState } from 'react';
import classnames from 'classnames';
import { LiraAdvContainer } from '@sbt-web/adv';
import { useViewport } from '@sbt-web/hooks';
import type { AdItem, CarInfo } from '@sbt-web/network/types';
import { Divider } from '@sbt-web/ui';
import { formatMainData } from '@sbt-web/utils';
import { AdsAdDetailsBlock } from '@/client/components/Adv/AdSense/adDetails/AdDetailsBlock';
import { AdsGAMPlacement } from '@/client/components/Adv/GPT';
import { LiraContainerIds } from '@/client/components/Adv/Lira/liraContainerIds';
import { LiraLazySlotBelowGalleryForDesktop } from '@/client/components/Adv/Lira/LiraLazySlotBelowGalleryForDesktop';
import { useLiraLazyOverlayGallery } from '@/client/components/Adv/Lira/useLiraLazyOverlayGallery';
import { AdDetailCarousel } from '@/client/components/Detail/AdDetailCarousel';
import { RecommendedAds } from '@/client/components/Detail/RecommendedAds';
import { labelProps as relatedLabelProps } from '@/client/components/Detail/RelatedAds';
import flattenFeatures from '@/shared/helpers/FlattenFeatures';
import { removeInfoCarDuplication } from '@/shared/helpers/RemoveInfoCarDuplication';
import { isPro } from '@/shared/models/Advertiser';
import { useAdvToggle } from '@/client/hooks/useAdvToggle';
import InternalLinksFooter from '../../SEO/InternalLinks/Footer';
import InternalLinksHeader from '../../SEO/InternalLinks/Header';
import NavigationRow from '../NavigationRow';
import qualitySeals from '../QualitySeal/quality-seals';
import StickyBar from '../StickyBar';
import { searchForRecommendation } from './../../../../server/detail-requests/item';
import type { Props as CommonProps } from './common-props';
import { AdvertiserInfoSection, BottomSection, DescriptionSection, GallerySection, GeneralInfoSection, InfocarSection, MainDataSection, MapSection, QualitySealSection, SkyscraperSection, VehicleHistorySection } from './Layout';
import { trackAutoClick, trackImpression } from './Layout/RelatedAds';
import css from './style/container.module.scss';
import gridStyles from './style/grid.module.scss';
type InfoCarDataProp = {
  infocarData: CarInfo | null;
};
export type Props = CommonProps & InfoCarDataProp;
const defaultDesktopRightBlockRowSpan = 6;
export function AutoContainer(t0) {
  const $ = _c(134);
  const {
    ad,
    infocarData: t1,
    internalLinks,
    shop,
    shopGoogleReviews,
    favoriteCounter,
    advertiserProfile,
    trustInfo,
    privateUserInfo,
    userAds
  } = t0;
  const infocarData = t1 === undefined ? null : t1;
  const {
    isMobile,
    isDesktop
  } = useViewport();
  const {
    noAdvToggle
  } = useAdvToggle();
  const noAdvAdvertiser = ad.advertiser.proPremiumDetailEnabled;
  const mapDetails = ad.geo.map;
  const hasMap = mapDetails?.longitude != null && mapDetails?.latitude != null;
  const qualitySealId = ad.features["/quality_seal"]?.values?.[0]?.key;
  const hasQualitySeal = qualitySealId != null && qualitySealId in qualitySeals;
  let t2;
  if ($[0] !== ad) {
    t2 = formatMainData(ad, {
      short: false,
      assetsBase: process.env.NEXT_PUBLIC_ASSETS_BASE_URL
    });
    $[0] = ad;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const mainFeatures = t2;
  const hasMainFeatures = mainFeatures.length > 0;
  const features = flattenFeatures(ad.features, ad.geo);
  const hasFeatures = features.length > 0;
  let t3;
  if ($[2] !== hasFeatures || $[3] !== hasMainFeatures || $[4] !== hasMap || $[5] !== hasQualitySeal || $[6] !== infocarData) {
    t3 = () => {
      let result = defaultDesktopRightBlockRowSpan;
      if (hasMainFeatures) {
        result = defaultDesktopRightBlockRowSpan + 1;
      }
      if (hasFeatures) {
        result = result + 2;
      }
      if (hasQualitySeal) {
        result = result + 1;
      }
      if (infocarData) {
        result = result + 2;
      }
      if (hasMap) {
        result = result + 1;
      }
      return result;
    };
    $[2] = hasFeatures;
    $[3] = hasMainFeatures;
    $[4] = hasMap;
    $[5] = hasQualitySeal;
    $[6] = infocarData;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const [desktopRightBlockRowSpan, setDesktopRightBlockRowSpan] = useState(t3);
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = [];
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const [recommendedAds, setRecommendedAds] = useState(t4);
  let t5;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => setDesktopRightBlockRowSpan(_temp);
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const increaseRowSpan = t5;
  let t6;
  if ($[10] !== ad || $[11] !== increaseRowSpan || $[12] !== recommendedAds) {
    t6 = () => {
      increaseRowSpan();
      trackImpression(ad, recommendedAds, "Engagement", "Recommendation widget impression", {
        transactionID: "sdrn:subito:recommendation:subito-suggested-ads",
        itemID: ad.urn,
        listName: "relateditems:subitov0"
      });
    };
    $[10] = ad;
    $[11] = increaseRowSpan;
    $[12] = recommendedAds;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const handleOnVisible = t6;
  let t7;
  if ($[14] !== ad || $[15] !== isMobile) {
    t7 = {
      ad,
      isMobile
    };
    $[14] = ad;
    $[15] = isMobile;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  useLiraLazyOverlayGallery(t7);
  let t8;
  let t9;
  if ($[17] !== ad) {
    t8 = () => {
      const price = Number.parseInt(ad.features["/price"]?.values[0]?.key ?? "0");
      const request = isPro(ad.advertiser.type) ? searchForRecommendation({
        pe: (price + 0.2 * price).toFixed(),
        ps: (price - 0.2 * price).toFixed(),
        uid: ad.advertiser.userId
      }) : searchForRecommendation({
        cb: ad.features["/car"]?.values[0]?.key?.toString(),
        cmo: ad.features["/car"]?.values[1]?.key?.toString(),
        pe: (price + 0.2 * price).toFixed(),
        ps: (price - 0.2 * price).toFixed(),
        r: ad.geo.region.id
      });
      request.then(searchResult => {
        const results = searchResult.filter(item => item.urn !== ad.urn);
        setRecommendedAds(results);
        trackImpression(ad, results, "View", "Recommendation widget impression", {
          transactionID: "sdrn:subito:recommendation:subito-suggested-ads",
          itemID: ad.urn,
          listName: "relateditems:subitov0"
        });
      });
    };
    t9 = [ad];
    $[17] = ad;
    $[18] = t8;
    $[19] = t9;
  } else {
    t8 = $[18];
    t9 = $[19];
  }
  useEffect(t8, t9);
  const infoCarNoDuplication = removeInfoCarDuplication(infocarData, features);
  let t10;
  if ($[20] !== internalLinks) {
    t10 = internalLinks != null ? <div className={classnames(gridStyles["detail-component"], gridStyles["navigation-detail-container"])}><InternalLinksHeader links={internalLinks.header} /></div> : null;
    $[20] = internalLinks;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = classnames(css["inner-ad-container"], gridStyles["detail-container"]);
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== desktopRightBlockRowSpan) {
    t12 = {
      "--right-block-row-span": desktopRightBlockRowSpan
    };
    $[23] = desktopRightBlockRowSpan;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = classnames([gridStyles["detail-component"], gridStyles["navigation-detail-container"]]);
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== ad) {
    t14 = <section className={t13}><NavigationRow item={ad} /></section>;
    $[26] = ad;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = [gridStyles["detail-component"], gridStyles.gallery];
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] !== ad) {
    t16 = <GallerySection classes={t15} ad={ad} />;
    $[29] = ad;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  let t17;
  if ($[31] !== isMobile || $[32] !== noAdvAdvertiser || $[33] !== noAdvToggle) {
    t17 = isMobile && noAdvToggle !== "B" && !noAdvAdvertiser && <AdsGAMPlacement classes={[gridStyles["detail-component"]]} id={LiraContainerIds.adView.anchorGallery} fixedHeight={50} />;
    $[31] = isMobile;
    $[32] = noAdvAdvertiser;
    $[33] = noAdvToggle;
    $[34] = t17;
  } else {
    t17 = $[34];
  }
  let t18;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = classnames(gridStyles["detail-component"], gridStyles["right-container"]);
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  let t19;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = [gridStyles["ad-info-container"], gridStyles["ad-info-override"]];
    $[36] = t19;
  } else {
    t19 = $[36];
  }
  let t20;
  if ($[37] !== ad || $[38] !== advertiserProfile || $[39] !== favoriteCounter || $[40] !== privateUserInfo || $[41] !== shop || $[42] !== shopGoogleReviews || $[43] !== trustInfo || $[44] !== userAds) {
    t20 = <GeneralInfoSection classes={t19} ad={ad} shop={shop} shopGoogleReviews={shopGoogleReviews} favoriteCounter={favoriteCounter} advertiserProfile={advertiserProfile} trustInfo={trustInfo} shippingCosts={null} privateUserInfo={privateUserInfo} userAds={userAds} />;
    $[37] = ad;
    $[38] = advertiserProfile;
    $[39] = favoriteCounter;
    $[40] = privateUserInfo;
    $[41] = shop;
    $[42] = shopGoogleReviews;
    $[43] = trustInfo;
    $[44] = userAds;
    $[45] = t20;
  } else {
    t20 = $[45];
  }
  let t21;
  if ($[46] !== ad || $[47] !== isDesktop) {
    t21 = isDesktop ? <SkyscraperSection item={ad} classes={[gridStyles.skyscraper]} /> : null;
    $[46] = ad;
    $[47] = isDesktop;
    $[48] = t21;
  } else {
    t21 = $[48];
  }
  let t22;
  if ($[49] !== t20 || $[50] !== t21) {
    t22 = <div className={t18}>{t20}{t21}</div>;
    $[49] = t20;
    $[50] = t21;
    $[51] = t22;
  } else {
    t22 = $[51];
  }
  let t23;
  if ($[52] !== noAdvAdvertiser || $[53] !== noAdvToggle) {
    t23 = noAdvToggle !== "B" && !noAdvAdvertiser && <LiraLazySlotBelowGalleryForDesktop cssClass={gridStyles["detail-component"]} />;
    $[52] = noAdvAdvertiser;
    $[53] = noAdvToggle;
    $[54] = t23;
  } else {
    t23 = $[54];
  }
  let t24;
  if ($[55] !== hasMainFeatures || $[56] !== mainFeatures) {
    t24 = hasMainFeatures ? <MainDataSection classes={[gridStyles["detail-component"]]} features={mainFeatures} /> : null;
    $[55] = hasMainFeatures;
    $[56] = mainFeatures;
    $[57] = t24;
  } else {
    t24 = $[57];
  }
  let t25;
  if ($[58] !== hasQualitySeal || $[59] !== qualitySealId) {
    t25 = hasQualitySeal ? <QualitySealSection classes={[gridStyles["detail-component"]]} qualitySealId={qualitySealId} /> : null;
    $[58] = hasQualitySeal;
    $[59] = qualitySealId;
    $[60] = t25;
  } else {
    t25 = $[60];
  }
  let t26;
  if ($[61] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = [gridStyles["detail-component"], gridStyles.description];
    $[61] = t26;
  } else {
    t26 = $[61];
  }
  let t27;
  if ($[62] !== ad) {
    t27 = <DescriptionSection ad={ad} classes={t26} />;
    $[62] = ad;
    $[63] = t27;
  } else {
    t27 = $[63];
  }
  let t28;
  if ($[64] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = <Divider spacing="null" classes={[gridStyles["detail-component"], gridStyles["section-divider"]]} />;
    $[64] = t28;
  } else {
    t28 = $[64];
  }
  let t29;
  if ($[65] !== isDesktop || $[66] !== noAdvAdvertiser || $[67] !== noAdvToggle) {
    t29 = isDesktop || noAdvToggle === "B" || noAdvAdvertiser ? null : <><LiraAdvContainer style={{
        minHeight: "250px"
      }} className={gridStyles["detail-component"]} idForAd={LiraContainerIds.adView.boxBelow1} inlineStyleForAd={{
        display: "block"
      }} /><Divider spacing="null" classes={[gridStyles["detail-component"], gridStyles["section-divider"]]} /></>;
    $[65] = isDesktop;
    $[66] = noAdvAdvertiser;
    $[67] = noAdvToggle;
    $[68] = t29;
  } else {
    t29 = $[68];
  }
  let t30;
  if ($[69] !== features || $[70] !== hasFeatures) {
    t30 = hasFeatures ? <VehicleHistorySection features={features} classes={[gridStyles["detail-component"], gridStyles["vehicle-history"]]} /> : null;
    $[69] = features;
    $[70] = hasFeatures;
    $[71] = t30;
  } else {
    t30 = $[71];
  }
  let t31;
  if ($[72] !== infoCarNoDuplication) {
    t31 = infoCarNoDuplication != null && <><Divider spacing="null" classes={[gridStyles["detail-component"], gridStyles["section-divider"]]} /><InfocarSection classes={[gridStyles["detail-component"], gridStyles.infocar]} infocarData={infoCarNoDuplication} /></>;
    $[72] = infoCarNoDuplication;
    $[73] = t31;
  } else {
    t31 = $[73];
  }
  let t32;
  if ($[74] !== hasMap || $[75] !== mapDetails) {
    t32 = hasMap ? <MapSection classes={[gridStyles["detail-component"], gridStyles.map]} map={mapDetails} /> : null;
    $[74] = hasMap;
    $[75] = mapDetails;
    $[76] = t32;
  } else {
    t32 = $[76];
  }
  let t33;
  if ($[77] !== isDesktop) {
    t33 = !isDesktop && <LiraAdvContainer style={{
      height: "250px"
    }} className={gridStyles["detail-component"]} idForAd={LiraContainerIds.adView.boxBelow3} />;
    $[77] = isDesktop;
    $[78] = t33;
  } else {
    t33 = $[78];
  }
  let t34;
  if ($[79] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = [gridStyles["detail-component"]];
    $[79] = t34;
  } else {
    t34 = $[79];
  }
  let t35;
  if ($[80] !== ad || $[81] !== advertiserProfile || $[82] !== shop || $[83] !== shopGoogleReviews || $[84] !== trustInfo) {
    t35 = <AdvertiserInfoSection classes={t34} item={ad} shop={shop} shopGoogleReviews={shopGoogleReviews} trustInfo={trustInfo} advertiserProfile={advertiserProfile} />;
    $[80] = ad;
    $[81] = advertiserProfile;
    $[82] = shop;
    $[83] = shopGoogleReviews;
    $[84] = trustInfo;
    $[85] = t35;
  } else {
    t35 = $[85];
  }
  let t36;
  if ($[86] !== ad || $[87] !== increaseRowSpan) {
    t36 = <RecommendedAds item={ad} className={gridStyles["detail-component"]} onActualRender={increaseRowSpan} />;
    $[86] = ad;
    $[87] = increaseRowSpan;
    $[88] = t36;
  } else {
    t36 = $[88];
  }
  let t37;
  if ($[89] !== ad) {
    t37 = (item_0, index) => {
      trackAutoClick(ad, item_0.urn, index, {
        transactionID: "sdrn:subito:recommendation:subito-suggested-ads",
        itemID: ad.urn,
        listName: "relateditems:subitov0"
      });
    };
    $[89] = ad;
    $[90] = t37;
  } else {
    t37 = $[90];
  }
  let t38;
  if ($[91] !== handleOnVisible || $[92] !== recommendedAds || $[93] !== t37) {
    t38 = <AdDetailCarousel {...relatedLabelProps} data={recommendedAds} className={gridStyles["detail-component"]} onVisible={handleOnVisible} onClick={t37} />;
    $[91] = handleOnVisible;
    $[92] = recommendedAds;
    $[93] = t37;
    $[94] = t38;
  } else {
    t38 = $[94];
  }
  let t39;
  if ($[95] !== t12 || $[96] !== t14 || $[97] !== t16 || $[98] !== t17 || $[99] !== t22 || $[100] !== t23 || $[101] !== t24 || $[102] !== t25 || $[103] !== t27 || $[104] !== t29 || $[105] !== t30 || $[106] !== t31 || $[107] !== t32 || $[108] !== t33 || $[109] !== t35 || $[110] !== t36 || $[111] !== t38) {
    t39 = <div className={t11} style={t12}>{t14}{t16}{t17}{t22}{t23}{t24}{t25}{t27}{t28}{t29}{t30}{t31}{t32}{t33}{t35}{t36}{t38}</div>;
    $[95] = t12;
    $[96] = t14;
    $[97] = t16;
    $[98] = t17;
    $[99] = t22;
    $[100] = t23;
    $[101] = t24;
    $[102] = t25;
    $[103] = t27;
    $[104] = t29;
    $[105] = t30;
    $[106] = t31;
    $[107] = t32;
    $[108] = t33;
    $[109] = t35;
    $[110] = t36;
    $[111] = t38;
    $[112] = t39;
  } else {
    t39 = $[112];
  }
  let t40;
  if ($[113] !== ad || $[114] !== isMobile) {
    t40 = !isMobile ? <><AdsAdDetailsBlock className={gridStyles["detail-component"]} /><StickyBar item={ad} /></> : null;
    $[113] = ad;
    $[114] = isMobile;
    $[115] = t40;
  } else {
    t40 = $[115];
  }
  let t41;
  if ($[116] !== isDesktop) {
    t41 = isDesktop ? null : <AdsGAMPlacement id={LiraContainerIds.adView.boxBelow2} />;
    $[116] = isDesktop;
    $[117] = t41;
  } else {
    t41 = $[117];
  }
  let t42;
  if ($[118] !== t39 || $[119] !== t40 || $[120] !== t41) {
    t42 = <div className={css["outer-ad-container"]}>{t39}{t40}{t41}</div>;
    $[118] = t39;
    $[119] = t40;
    $[120] = t41;
    $[121] = t42;
  } else {
    t42 = $[121];
  }
  let t43;
  if ($[122] !== ad) {
    t43 = <BottomSection item={ad} />;
    $[122] = ad;
    $[123] = t43;
  } else {
    t43 = $[123];
  }
  let t44;
  if ($[124] !== internalLinks) {
    t44 = internalLinks != null ? <InternalLinksFooter links={internalLinks.footer} /> : null;
    $[124] = internalLinks;
    $[125] = t44;
  } else {
    t44 = $[125];
  }
  let t45;
  if ($[126] !== isDesktop) {
    t45 = isDesktop ? <div id="apn_body" /> : null;
    $[126] = isDesktop;
    $[127] = t45;
  } else {
    t45 = $[127];
  }
  let t46;
  if ($[128] !== t10 || $[129] !== t42 || $[130] !== t43 || $[131] !== t44 || $[132] !== t45) {
    t46 = <>{t10}{t42}{t43}{t44}{t45}</>;
    $[128] = t10;
    $[129] = t42;
    $[130] = t43;
    $[131] = t44;
    $[132] = t45;
    $[133] = t46;
  } else {
    t46 = $[133];
  }
  return t46;
}
function _temp(span) {
  return span + 1;
}
export default AutoContainer;